import { useTheme } from '@emotion/react';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import {
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ConsultationEventListener from '../ConsultationEventListener';
import { pushSnackbar } from 'FRONTEND/actions/app';
import ConsultationSummary from 'FRONTEND/components/ConsultationSummary';
import { consultationRead } from 'FRONTEND/reducers/consultationSlice';
import {
  enterConsultationView,
  exitConsultationView,
  HISTORY_TAB_INDEX,
  navigate,
  SUMMARY_TAB_INDEX,
} from 'FRONTEND/reducers/consultationViewSlice';
import { getConsultationEvents } from 'FRONTEND/utils/backendApi/consultationApi';
import { extractConsultationParamsFromUrl } from 'FRONTEND/utils/extractConsultationParamsFromUrl';
import i18n from 'FRONTEND/utils/i18n';
import { PJAX_EVENTS_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID } from 'LEGACY/constants';

const TAB_COLOR = {
  [SUMMARY_TAB_INDEX]: 'tertiary',
  [HISTORY_TAB_INDEX]: 'secondary',
};

const refreshHistoryTab = async() => {
  const consultationRes = await getConsultationEvents(extractConsultationParamsFromUrl(window.location.href));
  const sequentialDiv = document.querySelector('#sequentialEvents');
  if (sequentialDiv) {
    sequentialDiv.innerHTML = consultationRes;
  }
};

const insertBadge = (color) => {
  const lastCreateEvent = document.querySelector('#sequentialEvents > section:last-child');
  const lastCreateEventHeader = lastCreateEvent.querySelector('div.panel-heading');
  const lastCreateEventTtitle = lastCreateEventHeader.firstElementChild;
  lastCreateEvent?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const badge = document.createElement('div');
  badge.classList.add('consultationBadge');
  badge.style.backgroundColor = color;
  badge.style.boxShadow = '0 0 5px 0' + color;
  lastCreateEventTtitle.prepend(badge);
};

const ConsultationNav = ({ rtcServerUrl }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  useEffect(() => {
    dispatch(consultationRead(extractConsultationParamsFromUrl(window.location.href))); // TODO: pass the consultation data via the template instead.
    dispatch(enterConsultationView());
    return () => {
      dispatch(exitConsultationView());
    };
  }, []);

  const handleTabClick = (event, newTabIndex) => {
    dispatch(navigate({ tabIndex: newTabIndex }));
  };

  const currentTabIndex = useSelector((state) => state.consultationView.currentTabIndex);

  const handleNewConsultationEvent = useCallback(async() => {
    dispatch(pushSnackbar({
      message: i18n._('New data is available'),
      variant: 'success',
    }));

    dispatch(consultationRead(extractConsultationParamsFromUrl(window.location.href)));
    if (currentTabIndex === HISTORY_TAB_INDEX) {
      await refreshHistoryTab();
      insertBadge(theme.palette.primary.main);
    }
  }, [currentTabIndex]);


  return (
    <>
      <Paper
        elevation={0}
        sx={(theme) => ({
          marginBottom: theme.spacing(3),
          borderRadius: theme.spacing(2),
          paddingX: theme.spacing(2),
        })}
      >
        {enterConsultationView != null && (
          <Tabs
            value={currentTabIndex}
            TabIndicatorProps={{
              sx: {
                backgroundColor: `${TAB_COLOR[currentTabIndex]}.main`,
              },
            }}
          >
            <Tab
              iconPosition='start'
              label={i18n._('Summary')}
              icon={<SummarizeIcon />}
              onClick={(event) => handleTabClick(event, SUMMARY_TAB_INDEX)}
            />
            <Tab
              iconPosition='start'
              label={i18n._('History')}
              icon={<HorizontalSplitIcon />}
              onClick={(event) => handleTabClick(event, HISTORY_TAB_INDEX)}
            />
          </Tabs>
        )}
      </Paper>

      {currentTabIndex === 0 && (
        <ConsultationSummary />
      )}
      {currentTabIndex === 1 && (
        <>
          <div id={PJAX_EVENTS_CONTAINER_ID} />
          <div id={PJAX_TRANSITION_CONTAINER_ID} />
        </>
      )}
      <ConsultationEventListener signalingUrl={rtcServerUrl} onNewConsultationEvent={handleNewConsultationEvent} />
    </>
  );
};

export default ConsultationNav;
